import { LitElement, html, nothing } from 'lit';
import { TagKey } from '@cof/tag-catalog-fs-personal-mfe';
import { stratum } from '../../../../constants/stratum.js';
import { DebugService } from '../../../shared/services/debug.service.js';
import { style } from './step-adress-v2.style.js';
import { ApplicationService } from '../../../services/application.service.js';
import '@cof/fs-radio-wc/fs-radio.js';
import './address-v2/address-v2.component.js';
import { globalStyle } from '../../../global-styles.style.js';
import { validateForm } from '../../../shared/decorators/validate-form.decorator.js';
import './toggle-share-address/toggle-share-address.component.js';
import { REFI_PERSONAL_CONST } from '../../../shared/constants/app.constants.js';

class RefiStepAddressComponent extends LitElement {
  constructor() {
    super();
    this.debug = new DebugService('RefiStepAddressComponent');
    this.validity = {};
    this.isCardOpen = false;
    this.pending = true;
  }

  static properties = {
    isSameAddress: { type: Boolean, reflect: true },
    pending: { type: Boolean, reflect: true },
  };

  static styles = [style, globalStyle];

  connectedCallback() {
    super.connectedCallback();

    // State initialization
    this.application = ApplicationService.Application;
    this.coAppPrefix = this.isPrimary ? '' : 'co-';
    this.isFinancial =
      this.application.profileType === REFI_PERSONAL_CONST.FINANCIAL;
    this.isJointApplication = this.application?.applicationType === 'JOINT';
    this.isSameAddress =
      typeof this.application.primaryApplicant.isSameAddress === 'boolean'
        ? this.application.primaryApplicant.isSameAddress
        : undefined;
    this.isNextEnabled = false;
    this.coEditCardTagFired = false;

    // Children element IDs
    this.childIds = {
      primaryAddressChildId: 'primary-form',
      ndiFieldsChildId: 'ndi-fields',
    };

    // If joint application, add more children accordingly
    if (this.isJointApplication) {
      this.childIds.sameAddressToggleId = 'share-address-toggle';
      this.childIds.coAppAddressChildId = 'coApp-form';
    } else {
      // Making sure these are null if not needed
      this.isSameAddress = null;
    }

    stratum.publishTag(TagKey.PAGE_VIEW_COAF_REFI_PREQUALIFY_PERSONAL_ADDRESS);

    // Setting pending to false after a short delay to prevent double clicks from progressing the form further
    setTimeout(() => {
      this.pending = false;
    }, 200);
  }

  render() {
    return html`
      <form
        id="step-address-v2-form"
        class="step-address-v2-form"
        @submit="${this.onSubmit}"
        novalidate
      >
        <refi-heading is-iconic>
          ${this.isFinancial
            ? html`Now, a few housing details`
            : html`Fill in your housing details`}
        </refi-heading>

        <refi-address-v2
          id="${this.childIds.primaryAddressChildId}"
          @validity="${this.updateValidity}"
          @edit-card-open="${this.editCardOpened}"
          @edit-card-closed="${this.editCardClosed}"
          applicant-type="primaryApplicant"
        ></refi-address-v2>
        <refi-address-ndi-v2
          id="${this.childIds.ndiFieldsChildId}"
          is-primary
          @validity="${this.updateValidity}"
        >
        </refi-address-ndi-v2>
        ${this.isJointApplication
          ? html`<refi-share-address-toggle
                id="${this.childIds.sameAddressToggleId}"
                .isSameAddress="${this.isSameAddress}"
                @toggle-same-address="${this.toggleSameAddress}"
              >
              </refi-share-address-toggle>
              <fs-body-text
                size="small"
                variant="feedback-negative"
                ?hidden="${this.validity[this.childIds.sameAddressToggleId] !==
                false}"
                id="sameAddressToggleInvalid"
              >
                Please select an option
              </fs-body-text> `
          : nothing}
        ${this.renderCoApplicantAddress()}
        <fs-button class="fs-button next-button" variant="primary">
          Next
        </fs-button>
      </form>
    `;
  }

  renderCoApplicantAddress() {
    if (this.isSameAddress === false) {
      return html`<div class="coborrower-card">
        <div class="coborrower-card-content">
          <fs-heading size="normal" level="2" variant="primary">
            Co-borrower address
          </fs-heading>
          <refi-address-v2
            id="${this.childIds.coAppAddressChildId}"
            ?is-same-address=${this.isSameAddress}
            @validity="${this.updateValidity}"
            applicant-type="coApplicant"
          >
          </refi-address-v2>
        </div>
      </div>`;
    }
    return html``;
  }

  toggleSameAddress(ev) {
    this.isSameAddress =
      typeof ev.detail.isSameAddress === 'boolean'
        ? ev.detail.isSameAddress
        : undefined;
    this.updateValidity({
      detail: {
        name: this.childIds.sameAddressToggleId,
        isValid: this.isSameAddress !== undefined,
      },
    });
    if (this.isSameAddress === true) {
      this.clearCoApplicantAddressValidity();
    }
    if (!this.isSameAddress && !this.coEditCardTagFired) {
      this.coEditCardTagFired = true;
      stratum.publishTag(TagKey.VIEW_COBORROWER_PERSONAL_EDIT);
    }
    if (this.isSameAddress) {
      this.coEditCardTagFired = false;
    }
    this.requestUpdate();
  }

  clearCoApplicantAddressValidity() {
    // Filter out keys starting with co- from this.validity
    this.validity = Object.keys(this.validity)
      .filter((key) => !key.startsWith('co-'))
      .reduce((acc, key) => {
        acc[key] = this.validity[key];
        return acc;
      }, {});
  }

  editCardOpened() {
    this.isCardOpen = true;
  }

  editCardClosed() {
    this.isCardOpen = false;
  }

  updateValidity(ev) {
    this.validity[ev.detail.name] = ev.detail.isValid;
    this.requestUpdate();
  }

  @validateForm()
  async onSubmit() {
    if (this.pending) {
      return;
    }

    this.debug.log('Checking all fields validity: ', this.validity);

    let valid = true;

    // Validate primary address fields
    if (this.isFinancial) {
      if (this.isCardOpen) {
        valid = false;
        this.shadowRoot
          .getElementById(this.childIds.primaryAddressChildId)
          .showEditError();
        return;
      }
    }
    this.runBusinessChecks();

    this.debug.log('validity results: ', this.validity);
    Object.values(this.validity).forEach((validityValue) => {
      if (!validityValue) {
        valid = false;
      }
    });

    this.debug.log('step-address valid and ready to save? ', valid);
    if (valid) {
      this.saveHousingInfoToApplication();
      stratum.publishTag(TagKey.ADDRESS_PAGE_CO_BORROWER_ADDRESS, {
        replacements: {
          value: this.isSameAddress ? 'sameAddress' : 'differentAddress',
        },
      });
      this.debug.log(
        '*** Application saved ****, application:  ',
        this.application
      );
      this.dispatchEvent(new CustomEvent('next-step'));
    }
  }

  saveHousingInfoToApplication() {
    Object.values(this.childIds).forEach((id) => {
      if (id === this.childIds.coAppAddressChildId && this.isSameAddress) {
        this.debug.log(
          'Skipping saving co-applicant address since same address is selected'
        );
        Object.assign(
          ApplicationService.Application.coApplicant.currentAddress,
          ApplicationService.Application.primaryApplicant.currentAddress
        );
      } else {
        this.debug.log('Saving... id: ', id);
        this.shadowRoot.getElementById(id).saveToApplication();
      }
    });
  }

  runBusinessChecks() {
    // validate primary address fields
    this.shadowRoot
      .getElementById(this.childIds.primaryAddressChildId)
      .runBusinessChecks();

    // validate same-address toggle is selected
    if (this.isJointApplication) {
      this.validity[this.childIds.sameAddressToggleId] =
        this.isSameAddress !== undefined;
    }

    // validate NDI fields
    this.shadowRoot
      .getElementById(this.childIds.ndiFieldsChildId)
      .runBusinessChecks();

    // validate Co-applicant address fields
    if (this.isSameAddress === false) {
      this.shadowRoot
        .getElementById(this.childIds.coAppAddressChildId)
        .runBusinessChecks();
    }
    this.requestUpdate();
  }
}

customElements.define('refi-step-address-v2', RefiStepAddressComponent);
